import supabase from './supabase';

class CustomerService {
  // Get all contacts with enhanced filtering
  async getContacts(filters = {}) {
    try {
      let query = supabase
        .from('contacts')
        .select(`
          *,
          lead_source:lead_sources(id, name),
          primary_service:primary_services(id, name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,organization_name.ilike.%${filters.search}%`);
      }
      
      if (filters.customerType) {
        query = query.eq('customer_type', filters.customerType);
      }

      if (filters.pipelineStage) {
        query = query.eq('pipeline_stage', filters.pipelineStage);
      }

      if (filters.priority) {
        query = query.eq('pipeline_priority', filters.priority);
      }

      if (filters.customerTier) {
        query = query.eq('customer_tier', filters.customerTier);
      }

      if (filters.serviceType) {
        query = query.eq('service_type', filters.serviceType);
      }

      if (filters.location) {
        query = query.or(`city.ilike.%${filters.location}%,state.ilike.%${filters.location}%,address.ilike.%${filters.location}%`);
      }

      // Status filter (replaces timeFilter)
      if (filters.status) {
        switch (filters.status) {
          case 'active':
            query = query.eq('is_archived', false);
            break;
          case 'archived':
            query = query.eq('is_archived', true);
            break;
          // 'all' or empty means no filter
        }
      } else {
        // Default to active only if no status specified
        query = query.eq('is_archived', false);
      }

      // Date range filters
      const now = new Date();
      if (filters.dateRange) {
        switch (filters.dateRange) {
          case 'last-7-days':
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            query = query.gte('created_at', sevenDaysAgo.toISOString());
            break;
          case 'last-30-days':
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            query = query.gte('created_at', thirtyDaysAgo.toISOString());
            break;
          case 'last-90-days':
            const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            query = query.gte('created_at', ninetyDaysAgo.toISOString());
            break;
          case 'this-month':
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            query = query.gte('created_at', startOfMonth.toISOString());
            break;
          case 'last-month':
            const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            query = query
              .gte('created_at', startOfLastMonth.toISOString())
              .lte('created_at', endOfLastMonth.toISOString());
            break;
          case 'last-3-months':
            const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
            query = query.gte('created_at', threeMonthsAgo.toISOString());
            break;
        }
      }

      // Lead source filter
      if (filters.leadSource) {
        // Need to join with lead_sources and filter by name
        const { data: leadSourceData } = await supabase
          .from('lead_sources')
          .select('id')
          .eq('name', filters.leadSource)
          .single();
        
        if (leadSourceData) {
          query = query.eq('lead_source_id', leadSourceData.id);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching contacts:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getContacts:', error);
      throw error;
    }
  }

  // Get contact by ID with full details
  async getContactById(contactId) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          lead_source:lead_sources(id, name),
          primary_service:primary_services(id, name)
        `)
        .eq('id', contactId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch contact details' };
    }
  }

  // Create new contact
  async createContact(contactData, organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      // Check for duplicate email within the organization
      const { data: existingContact } = await supabase
        .from('contacts')
        .select('id, email')
        .eq('organization_id', orgId)
        .eq('email', contactData.email)
        .single();

      if (existingContact) {
        throw new Error(`A contact with email ${contactData.email} already exists in your organization`);
      }

      // Prepare the contact data for database insertion
      const dbContactData = {
        organization_id: orgId,
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        address: contactData.address,
        city: contactData.city,
        state: contactData.state,
        zip_code: contactData.zipCode,
        customer_type: contactData.customerType || 'individual',
        customer_tier: contactData.customerTier || 'Standard',
        organization_name: contactData.organizationName || null,
        website: contactData.website || null,
        industry: contactData.industry || null,
        organization_size: contactData.organizationSize || null,
        service_type: contactData.serviceType || null,
        preferred_contact_method: contactData.preferredContactMethod || 'email',
        pipeline_stage: contactData.pipeline_stage || 'Lead',
        pipeline_priority: contactData.pipeline_priority || 'medium',
        estimated_value: contactData.estimatedValue ? parseFloat(contactData.estimatedValue) : null,
        notes: contactData.notes || null,
        last_contact_date: new Date().toISOString(),
        lead_source_id: contactData.leadSource?.id || null,
        primary_service_id: contactData.primaryService?.id || null
      };

      const { data, error } = await supabase
        .from('contacts')
        .insert([dbContactData])
        .select()
        .single();

      if (error) {
        console.error('Error creating contact:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createContact:', error);
      return { success: false, error: error.message || 'Failed to create contact' };
    }
  }

  // Update contact
  async updateContact(id, contactData) {
    try {
      // Prepare the contact data for database update
      const dbContactData = {
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        address: contactData.address,
        city: contactData.city,
        state: contactData.state,
        zip_code: contactData.zipCode,
        customer_type: contactData.customerType || 'individual',
        customer_tier: contactData.customerTier || 'Standard',
        organization_name: contactData.organizationName || null,
        website: contactData.website || null,
        industry: contactData.industry || null,
        organization_size: contactData.organizationSize || null,
        service_type: contactData.serviceType || null,
        preferred_contact_method: contactData.preferredContactMethod || 'email',
        estimated_value: contactData.estimatedValue ? parseFloat(contactData.estimatedValue) : null,
        notes: contactData.notes || null,
        updated_at: new Date().toISOString(),
        lead_source_id: contactData.leadSourceId,
        primary_service_id: contactData.primaryServiceId,
      };

      const { data, error } = await supabase
        .from('contacts')
        .update(dbContactData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating contact:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateContact:', error);
      return { success: false, error: error.message || 'Failed to update contact' };
    }
  }

  // Delete a contact
  async deleteContact(id) {
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting contact:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteContact:', error);
      throw error;
    }
  }

  // Get contact deletion impact (what will be deleted)
  async getContactDeletionImpact(id) {
    try {
      const { data: contact, error: contactError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .single();
  
      if (contactError) {
        console.error('Error fetching contact for deletion impact:', contactError);
        throw contactError;
      }
  
      // NEW: Check for associated reviews
      const { count: reviewCount, error: reviewError } = await supabase
        .from('reviews')
        .select('id', { count: 'exact' })
        .eq('contact_id', id);
  
      if (reviewError) {
        console.error('Error checking for reviews:', reviewError);
        // Decide if you want to throw or continue. For now, we'll continue but block deletion.
      }
  
      // Contact deletion validation - reviews only (appointments table removed)
  
      const canDelete = (reviewCount === 0);
  
      return {
        contact,
        reviewCount: reviewCount || 0,
        canDelete,
        reason: canDelete ? 'No critical data found.' : 'This contact has submitted one or more reviews, which cannot be deleted.'
      };
    } catch (error) {
      console.error('Error in getContactDeletionImpact:', error);
      throw error;
    }
  }

  // Enhanced delete contact with validation
  async deleteContactWithValidation(id, reason = 'Manual deletion') {
    try {
      // First get the deletion impact
      const impact = await this.getContactDeletionImpact(id);

      // SERVER-SIDE CHECK: Prevent deletion if reviews exist
      if (!impact.canDelete) {
        throw new Error('This contact cannot be deleted because they have existing reviews. Please archive the contact instead.');
      }
      
      // Log the deletion for audit purposes
      console.log(`Deleting contact ${id}: ${impact.contact.name}`, {
        appointmentCount: impact.appointmentCount,
        reason
      });

      // Perform the deletion
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting contact:', error);
        throw error;
      }

      return {
        success: true,
        impact
      };
    } catch (error) {
      console.error('Error in deleteContactWithValidation:', error);
      throw error;
    }
  }

  // Archive a contact
  async archiveContact(id, reason = 'Manual archive') {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          archived_reason: reason,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error archiving contact:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in archiveContact:', error);
      throw error;
    }
  }

  // Bulk archive contacts
  async bulkArchiveContacts(ids, reason = 'Bulk archive') {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          archived_reason: reason,
        })
        .in('id', ids)
        .select();

      if (error) {
        console.error('Error bulk archiving contacts:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in bulkArchiveContacts:', error);
      throw error;
    }
  }

  // Restore archived contact
  async restoreContact(id) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .update({
          is_archived: false,
          archived_at: null,
          archived_reason: null,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error restoring contact:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in restoreContact:', error);
      throw error;
    }
  }

  // Bulk restore contacts
  async bulkRestoreContacts(ids) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .update({
          is_archived: false,
          archived_at: null,
          archived_reason: null,
        })
        .in('id', ids)
        .select();

      if (error) {
        console.error('Error bulk restoring contacts:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in bulkRestoreContacts:', error);
      throw error;
    }
  }

  // Get pipeline stages for organization
  async getPipelineStages(organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      const { data, error } = await supabase
        .from('pipeline_stages')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('position');

      if (error) {
        console.error('Error fetching pipeline stages:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getPipelineStages:', error);
      throw error;
    }
  }

  // Update pipeline stages
  async updatePipelineStages(stages, organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      // Get existing stages for this organization
      const { data: existingStages, error: fetchError } = await supabase
        .from('pipeline_stages')
        .select('*')
        .eq('organization_id', orgId);

      if (fetchError) {
        console.error('Error fetching existing stages:', fetchError);
        throw fetchError;
      }

      // Create a map of existing stages by ID (excluding temp IDs)
      const existingStageMap = new Map();
      existingStages.forEach(stage => {
        existingStageMap.set(stage.id, stage);
      });

      // Prepare operations
      const stagesToInsert = [];
      const stagesToUpdate = [];
      const stageIdsToKeep = new Set();

      stages.forEach((stage, index) => {
        const cleanStage = {
          organization_id: orgId,
          name: stage.name,
          color: stage.color,
          icon: stage.icon,
          position: index + 1,
          is_active: stage.is_active !== false, // Default to true if not specified
          triggers_review: stage.triggers_review || false
        };

        // Check if this is an existing stage (has a valid UUID that exists in DB)
        if (stage.id && !stage.id.startsWith('temp-') && existingStageMap.has(stage.id)) {
          // This is an update
          cleanStage.id = stage.id;
          cleanStage.updated_at = new Date().toISOString();
          stagesToUpdate.push(cleanStage);
          stageIdsToKeep.add(stage.id);
        } else {
          // This is a new stage
          stagesToInsert.push(cleanStage);
        }
      });

      // Find stages to delete (existing stages not in the new list)
      const stageIdsToDelete = existingStages
        .filter(stage => !stageIdsToKeep.has(stage.id))
        .map(stage => stage.id);

      console.log('Pipeline stages update plan:', {
        toInsert: stagesToInsert.length,
        toUpdate: stagesToUpdate.length,
        toDelete: stageIdsToDelete.length
      });

      // Execute operations in transaction-like manner
      const operations = [];

      // Delete removed stages
      if (stageIdsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('pipeline_stages')
          .delete()
          .in('id', stageIdsToDelete);

        if (deleteError) {
          console.error('Error deleting stages:', deleteError);
          throw deleteError;
        }
      }

      // Update existing stages
      for (const stage of stagesToUpdate) {
        const { error: updateError } = await supabase
          .from('pipeline_stages')
          .update(stage)
          .eq('id', stage.id);

        if (updateError) {
          console.error('Error updating stage:', updateError);
          throw updateError;
        }
      }

      // Insert new stages
      if (stagesToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('pipeline_stages')
          .insert(stagesToInsert);

        if (insertError) {
          console.error('Error inserting stages:', insertError);
          throw insertError;
        }
      }

      // Fetch and return updated stages
      const { data: updatedStages, error: finalFetchError } = await supabase
        .from('pipeline_stages')
        .select('*')
        .eq('organization_id', orgId)
        .order('position');

      if (finalFetchError) {
        console.error('Error fetching updated stages:', finalFetchError);
        throw finalFetchError;
      }

      return updatedStages;
    } catch (error) {
      console.error('Error in updatePipelineStages:', error);
      throw error;
    }
  }

  // Auto-archive old customers
  async autoArchiveCustomers() {
    try {
      const { error } = await supabase.rpc('auto_archive_customers');

      if (error) {
        console.error('Error auto-archiving customers:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in autoArchiveCustomers:', error);
      throw error;
    }
  }

  // Check if a new review request can be sent to a contact.
  async checkCanSendRequest(contactId, timeBufferDays = 7) {
    try {
      // 1. Get all review requests for this contact, ordered by most recent
      const { data: requests, error: requestError } = await supabase
        .from('review_requests')
        .select('status, sent_at')
        .eq('customer_id', contactId)
        .order('sent_at', { ascending: false });

      if (requestError) {
        console.error('Error fetching review requests for check:', requestError);
        throw requestError;
      }

      if (!requests || requests.length === 0) {
        // No previous requests, so we can definitely send one.
        return { canSend: true, reason: 'No previous review requests found.' };
      }

      // 2. Check for an active, unanswered request
      const activeRequest = requests.find(req => 
        req.status === 'pending' || req.status === 'submitted' || req.status === 'scheduled'
      );

      if (activeRequest) {
        // There is an active request that hasn't been completed or expired.
        return { 
          canSend: false, 
          reason: `An active review request is currently pending (Status: ${activeRequest.status}).` 
        };
      }
      
      // 3. Check if the most recent request was sent within the time buffer
      const mostRecentRequest = requests[0];
      const sentDate = new Date(mostRecentRequest.sent_at);
      const now = new Date();
      const daysDiff = (now - sentDate) / (1000 * 60 * 60 * 24);

      if (daysDiff <= timeBufferDays) {
        // The last request was sent too recently.
        return {
          canSend: false,
          reason: `A review request was already sent recently (${Math.floor(daysDiff)} days ago).`
        };
      }

      // 4. If all checks pass, a new request can be sent.
      return { canSend: true, reason: 'No active or recent requests found.' };

    } catch (error) {
      console.error('Error in checkCanSendRequest:', error);
      throw error;
    }
  }

  // Get review trigger stages for organization
  async getReviewTriggerStages(organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      const { data, error } = await supabase
        .from('pipeline_stages')
        .select('*')
        .eq('organization_id', orgId)
        .eq('triggers_review', true)
        .order('position');

      if (error) {
        console.error('Error fetching review trigger stages:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getReviewTriggerStages:', error);
      throw error;
    }
  }

  // Helper method to determine if a stage should be excluded from estimated value calculations
  isClosedStage(stageName) {
    if (!stageName) return false;
    
    const closedStageKeywords = [
      'lost', 'cancelled', 'canceled', 'closed', 'rejected', 'declined', 
      'abandoned', 'expired', 'withdrawn', 'terminated', 'failed',
      'no-sale', 'no sale', 'dead', 'inactive'
    ];
    
    const normalizedStage = stageName.toLowerCase().trim();
    return closedStageKeywords.some(keyword => normalizedStage.includes(keyword));
  }

  // Get contacts with active estimated values (excluding closed stages)
  getActiveEstimatedValue(contacts) {
    return contacts
      .filter(contact => !contact.is_archived && !this.isClosedStage(contact.pipeline_stage))
      .reduce((sum, contact) => sum + (contact.estimated_value || 0), 0);
  }

  // Get contact statistics
  async getContactStats(organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      const { data, error } = await supabase
        .from('contacts')
        .select('pipeline_stage, customer_type, is_archived, estimated_value')
        .eq('organization_id', orgId);

      if (error) {
        console.error('Error fetching contact stats:', error);
        throw error;
      }

      // Calculate statistics
      const stats = {
        total: data.length,
        active: data.filter(c => !c.is_archived).length,
        archived: data.filter(c => c.is_archived).length,
        individuals: data.filter(c => c.customer_type === 'individual').length,
        organizations: data.filter(c => c.customer_type === 'organization').length,
        totalEstimatedValue: this.getActiveEstimatedValue(data),
        byStage: {}
      };

      // Group by pipeline stage
      data.forEach(contact => {
        if (!stats.byStage[contact.pipeline_stage]) {
          stats.byStage[contact.pipeline_stage] = 0;
        }
        stats.byStage[contact.pipeline_stage]++;
      });

      return stats;
    } catch (error) {
      console.error('Error in getContactStats:', error);
      throw error;
    }
  }

  // Update contact pipeline stage
  async updateContactStage(id, stage, priority = null) {
    try {
      const updates = {
        pipeline_stage: stage,
        last_contact_date: new Date().toISOString(),
      };

      if (priority) {
        updates.pipeline_priority = priority;
      }

      const { data, error } = await supabase
        .from('contacts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating contact stage:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateContactStage:', error);
      throw error;
    }
  }

  // ==========================
  // CRM-OPTIMIZED METHODS
  // ==========================

  // Get contacts optimized for pipeline view (CRM)
  async getCRMPipelineData(filters = {}) {
    try {
      let query = supabase
        .from('contacts')
        .select(`
          id,
          name,
          email,
          phone,
          organization_name,
          pipeline_stage,
          pipeline_stage_color,
          pipeline_priority,
          estimated_value,
          last_contact_date,
          next_follow_up,
          lead_source,
          customer_type,
          is_archived,
          created_at,
          lead_source:lead_sources(id, name),
          primary_service:primary_services(id, name)
        `)
        .eq('is_archived', false)
        .order('pipeline_priority', { ascending: true })
        .order('last_contact_date', { ascending: false });

      // Apply CRM-specific filters
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,organization_name.ilike.%${filters.search}%`);
      }
      
      if (filters.pipelineStage) {
        query = query.eq('pipeline_stage', filters.pipelineStage);
      }

      if (filters.priority) {
        query = query.eq('pipeline_priority', filters.priority);
      }

      if (filters.minValue) {
        query = query.gte('estimated_value', filters.minValue);
      }

      if (filters.maxValue) {
        query = query.lte('estimated_value', filters.maxValue);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching CRM pipeline data:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCRMPipelineData:', error);
      throw error;
    }
  }

  // Get pipeline-specific statistics for CRM
  async getCRMPipelineStats(organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      const { data, error } = await supabase
        .from('contacts')
        .select('pipeline_stage, estimated_value, is_archived, created_at, customer_type')
        .eq('organization_id', orgId)
        .eq('is_archived', false);

      if (error) {
        console.error('Error fetching CRM pipeline stats:', error);
        throw error;
      }

      // Calculate CRM-specific statistics
      const totalLeads = data.length;
      const activePipeline = data.filter(c => c.estimated_value && c.estimated_value > 0).length;
      const totalValue = this.getActiveEstimatedValue(data);
      const organizations = data.filter(c => c.customer_type === 'organization').length;

      // Calculate stage distribution
      const stageDistribution = {};
      data.forEach(contact => {
        if (!stageDistribution[contact.pipeline_stage]) {
          stageDistribution[contact.pipeline_stage] = {
            count: 0,
            totalValue: 0
          };
        }
        stageDistribution[contact.pipeline_stage].count++;
        stageDistribution[contact.pipeline_stage].totalValue += contact.estimated_value || 0;
      });

      return {
        totalLeads,
        activePipeline,
        organizations,
        totalValue,
        stageDistribution
      };
    } catch (error) {
      console.error('Error in getCRMPipelineStats:', error);
      throw error;
    }
  }

  // ==========================
  // CLIENTS-OPTIMIZED METHODS
  // ==========================

  // Get contacts optimized for table view (Clients)
  async getClientsTableData(filters = {}) {
    try {
      let query = supabase
        .from('contacts')
        .select(`
          *,
          lead_source:lead_sources(id, name),
          primary_service:primary_services(id, name)
        `)
        .order('created_at', { ascending: false });

      // Apply client-specific filters
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,organization_name.ilike.%${filters.search}%,address.ilike.%${filters.search}%,city.ilike.%${filters.search}%`);
      }
      
      if (filters.customerType) {
        query = query.eq('customer_type', filters.customerType);
      }

      if (filters.customerTier) {
        query = query.eq('customer_tier', filters.customerTier);
      }

      if (filters.serviceType) {
        // Get current user's organization ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        const orgId = orgData?.organization_id;
        
        if (orgId) {
          // First get the service ID by name, then filter
          const { data: serviceData, error: serviceError } = await supabase
            .from('primary_services')
            .select('id')
            .eq('name', filters.serviceType)
            .eq('organization_id', orgId)
            .single();
          
          if (serviceData && !serviceError) {
            query = query.eq('primary_service_id', serviceData.id);
          }
        }
      }

      if (filters.location) {
        query = query.or(`city.ilike.%${filters.location}%,state.ilike.%${filters.location}%,address.ilike.%${filters.location}%`);
      }

      // Status filter
      if (filters.status) {
        switch (filters.status) {
          case 'active':
            query = query.eq('is_archived', false);
            break;
          case 'archived':
            query = query.eq('is_archived', true);
            break;
          case 'all':
            // No filter
            break;
        }
      } else {
        // Default to active only
        query = query.eq('is_archived', false);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching clients table data:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClientsTableData:', error);
      throw error;
    }
  }

  // Get client-specific statistics
  async getClientsStats(organizationId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      const { data, error } = await supabase
        .from('contacts')
        .select('customer_type, customer_tier, service_type, is_archived, created_at')
        .eq('organization_id', orgId);

      if (error) {
        console.error('Error fetching clients stats:', error);
        throw error;
      }

      // Calculate client-specific statistics
      const totalClients = data.length;
      const activeClients = data.filter(c => !c.is_archived).length;
      const archivedClients = data.filter(c => c.is_archived).length;
      const newThisMonth = data.filter(c => {
        const createdDate = new Date(c.created_at);
        const now = new Date();
        return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
      }).length;

      // Calculate tier distribution
      const tierDistribution = {};
      data.forEach(contact => {
        const tier = contact.customer_tier || 'Standard';
        if (!tierDistribution[tier]) {
          tierDistribution[tier] = 0;
        }
        tierDistribution[tier]++;
      });

      // Calculate service type distribution
      const serviceDistribution = {};
      data.forEach(contact => {
        const service = contact.service_type || 'Other';
        if (!serviceDistribution[service]) {
          serviceDistribution[service] = 0;
        }
        serviceDistribution[service]++;
      });

      return {
        totalClients,
        activeClients,
        archivedClients,
        newThisMonth,
        tierDistribution,
        serviceDistribution
      };
    } catch (error) {
      console.error('Error in getClientsStats:', error);
      throw error;
    }
  }
}

export default new CustomerService();